/**
 * L5 «Работа» — пятый уровень, впервые получивший собственный адрес.
 *
 * До него работа жила карточкой внутри очереди проверки: ссылка «эта работа»
 * вела в список, где её надо было найти глазами, а цепочка «контур → работа →
 * назад» не замыкалась. Ограничение объявлялось честно (`levels.ts`), потому
 * что сервер не отдавал работу ресурсом. Теперь отдаёт —
 * `/farm-obligations/{id}` отвечает уровнем целиком, — и адрес перестал быть
 * обещанием, которое некому выполнить.
 *
 * Порядок блоков — порядок вопросов, а не состав выдачи: «что за работа» →
 * «чьё окно и истекло ли» → «что сделано, а что нет» → «по каким контурам» →
 * «кто это внёс». Ровно этот порядок объявлен уровнем в `screen-levels.md`.
 *
 * Чего здесь нет по объявленному отказу уровня:
 *
 * * **никакой агрегации выше самой работы.** Ни доли в кампании, ни доли в
 *   районе: это величины уровней выше, и второй их источник здесь однажды
 *   разошёлся бы с первым;
 * * **«выполнено» одной осью.** Развилка рисуется только `DoneSplit`, и
 *   показать ею одну ось физически нельзя;
 * * **кадра карты.** Геометрия адресуется рамкой (`/map/contours?bbox=`), а не
 *   списком контуров: собрать рамку по контурам работы на клиенте значило бы
 *   выдумать её. Геометрия каждого контура остаётся на его уровне, куда ведёт
 *   номер в таблице.
 */

import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useAnalysisContext } from "../../analysis/context";
import { api, type ObligationContourOut } from "../../api/client";
import { useRequest } from "../../api/useRequest";
import { PageHeader } from "../../components/AppShell";
import { DetailDrawer } from "../../components/DetailDrawer";
import { Disclosure } from "../../components/Disclosure";
import { districtStep, LevelTrail, type TrailStep } from "../../components/LevelTrail";
import { LevelPocket } from "../ai/LevelPocket";
import { AreaProgress, AxisLegend, DoneSplit } from "../../components/Progress";
import { StatusMark } from "../../components/StatusMark";
import {
  Area,
  Badge,
  Button,
  Card,
  Count,
  DataStatePanel,
  DefinitionList,
  EvidenceBadge,
  InlineAlert,
  Text,
} from "../../components/ui";
import { formatDate } from "../../lib/format";
import { cropLabel } from "../../lib/crops";
import { DENSITY, levelPath, preview } from "../../lib/levels";
import { windowNote } from "../../lib/progress";
import { ROUTES } from "../../lib/routes";
import {
  campaignStatusLabel,
  datePrecisionLabel,
  deadlineKindLabel,
  workCategoryLabel,
} from "../../lib/workVocab";
import styles from "../features.module.css";
import { outcomePast, roleView } from "../verification/verificationModel";
import { progressLabel } from "./worksModel";
import { defineMessages } from "../../i18n/define";
import { COMMON } from "../../i18n/common";
import { useT } from "../../i18n/hooks";
import { tr } from "../../i18n/runtime";

const m = defineMessages({
  stVerified: {
    id: "canonWorks.detail.contour.verified",
    defaultMessage: "проверено",
    description: "Отметка в строке контура на карточке работы: есть суждение проверяющего (коротко, строчными)",
  },
  stAwaiting: {
    id: "canonWorks.detail.contour.awaiting",
    defaultMessage: "ждёт проверки",
    description: "Отметка в строке контура на карточке работы: отчёт есть, проверяющий ещё не смотрел (коротко)",
  },
  stReported: {
    id: "canonWorks.detail.contour.reported",
    defaultMessage: "отчёт есть",
    description: "Отметка в строке контура на карточке работы: отчёт внесён (коротко)",
  },
  stNone: {
    id: "canonWorks.detail.contour.none",
    defaultMessage: "без отчёта",
    description: "Отметка в строке контура на карточке работы: отчёта нет (это не «не сделано»; коротко)",
  },
  workOnField: {
    id: "canonWorks.detail.contour.workOnField",
    defaultMessage: "работа на поле #{id}",
    description: "Подпись строки контура, у которого нет номера; {id} — номер работы на поле (поручения работы полю)",
  },
  partOf: {
    id: "canonWorks.detail.contour.part",
    defaultMessage: "{base} · часть #{part}",
    description: "Подпись строки контура с частью; {base} — номер контура, {part} — номер части",
  },
  loading: {
    id: "canonWorks.detail.loading",
    defaultMessage: "Загружаем карточку работы",
    description: "Заголовок индикатора загрузки карточки работы",
  },
  notFound: {
    id: "canonWorks.detail.notFound",
    defaultMessage: "Такой работы нет",
    description: "Заголовок пустого состояния: работа с таким номером не найдена (404)",
  },
  loadFailed: {
    id: "canonWorks.detail.loadFailed",
    defaultMessage: "Карточка не загрузилась",
    description: "Заголовок ошибки загрузки карточки работы",
  },
  toQueue: {
    id: "canonWorks.detail.toQueue",
    defaultMessage: "К очереди проверки",
    description: "Кнопка/ссылка: вернуться к очереди проверки (инспектор, менеджер); коротко",
  },
  showDemo: {
    id: "canonWorks.detail.showDemo",
    defaultMessage: "Показать с демо-данными",
    description: "Кнопка на странице «Такой работы нет»: включить показ демонстрационных данных; коротко",
  },
  demoHidden: {
    id: "canonWorks.detail.demoHidden",
    defaultMessage: "Показ демо-данных выключен: если работа демонстрационная, в этом срезе её нет.",
    description: "Пояснение к ошибке 404 на карточке работы; срез — снимок данных на дату",
  },
  massifs: {
    id: "canonWorks.detail.trail.massifs",
    defaultMessage: "МАССИВЫ",
    description: "Надпись над несколькими массивами в цепочке уровней карточки работы; ЗАГЛАВНЫМИ, коротко",
  },
  massifCount: {
    id: "canonWorks.detail.trail.massifCount",
    defaultMessage: "{count, plural, one {# массив} few {# массива} many {# массивов} other {# массива}}",
    description: "Ступень цепочки уровней карточки работы: сколько массивов затрагивает работа",
  },
  massifUnknown: {
    id: "canonWorks.detail.trail.massifUnknown",
    defaultMessage: "не определён",
    description: "Ступень цепочки уровней карточки работы: массив не определён; коротко",
  },
  farmFallback: {
    id: "canonWorks.detail.trail.farmFallback",
    defaultMessage: "хозяйство #{id}",
    description: "Подпись ступени «хозяйство» в цепочке уровней, если у хозяйства нет названия; {id} — номер",
  },
  purposeAsOf: {
    id: "canonWorks.detail.purpose.asOf",
    defaultMessage: "Состояние на {date}, срез {version}.",
    description: "Подзаголовок карточки работы; {date} — дата, {version} — версия правил среза (срез — снимок данных на дату)",
  },
  purposeNow: {
    id: "canonWorks.detail.purpose.now",
    defaultMessage: "Состояние «как знаем сейчас»; версии утверждений не отсечены по дате.",
    description: "Подзаголовок карточки работы, когда дата среза не задана",
  },
  notSpecified: {
    id: "canonWorks.detail.notSpecified",
    defaultMessage: "не указан",
    description: "Подставляется вместо версии среза, когда она не указана; строчными",
  },
  reviewInQueue: {
    id: "canonWorks.detail.reviewInQueue",
    defaultMessage: "Разобрать в очереди",
    description: "Кнопка на карточке работы: открыть эту работу в очереди проверки (инспектор, менеджер); коротко",
  },
  demoAlertTitle: {
    id: "canonWorks.detail.demo.title",
    defaultMessage: "ДЕМО: синтетическая работа",
    description: "Заголовок предупреждения на карточке демонстрационной работы",
  },
  demoAlertText: {
    id: "canonWorks.detail.demo.text",
    defaultMessage: "Эта работа принадлежит демо-каскаду и не входит ни в один фактический итог района.",
    description: "Текст предупреждения на карточке демонстрационной работы",
  },
  whatTitle: {
    id: "canonWorks.detail.what.title",
    defaultMessage: "Что это за работа",
    description: "Заголовок первой карточки на экране работы",
  },
  catalogWork: {
    id: "canonWorks.detail.what.kind",
    defaultMessage: "Вид работы",
    description: "Название строки: вид работы из справочника (вспашка, посев…)",
  },
  catalogNote: {
    id: "canonWorks.detail.what.kindNote",
    defaultMessage: "код {code}, категория {category}",
    description: "Пояснение к виду работы; {code} — код из справочника, {category} — категория работы",
  },
  noCatalog: {
    id: "canonWorks.detail.what.noKind",
    defaultMessage: "сервер не сообщает вид работы",
    description: "Пометка, когда справочник вида работы не пришёл",
  },
  planWork: {
    id: "canonWorks.detail.what.planWork",
    defaultMessage: "Работа плана",
    description: "Название строки: пункт плана агроцикла (этап, порядок), к которому относится работа",
  },
  noPlanLabel: {
    id: "canonWorks.detail.what.noPlanLabel",
    defaultMessage: "подписи работы плана нет",
    description: "Пометка рядом с номером работы плана, когда у неё нет названия",
  },
  templateCrop: {
    id: "canonWorks.detail.what.templateCrop",
    defaultMessage: "шаблон, сезон {season}, культура {crop}",
    description: "Строка под работой плана; {season} — сезон (год), {crop} — культура",
  },
  templateNoCrop: {
    id: "canonWorks.detail.what.templateNoCrop",
    defaultMessage: "шаблон, сезон {season}, вне агроцикла",
    description: "Строка под работой плана; шаблон без культуры — работа вне агроцикла; {season} — сезон (год)",
  },
  required: {
    id: "canonWorks.detail.what.required",
    defaultMessage: "обязательная",
    description: "Бейдж: работа плана обязательна; коротко",
  },
  optional: {
    id: "canonWorks.detail.what.optional",
    defaultMessage: "необязательная",
    description: "Бейдж: работа плана необязательна; коротко",
  },
  noPlan: {
    id: "canonWorks.detail.what.noPlan",
    defaultMessage: "сервер не сообщает работу плана",
    description: "Пометка, когда данные работы плана не пришли",
  },
  seasonUnknown: {
    id: "canonWorks.detail.what.seasonUnknown",
    defaultMessage: "не указан",
    description: "Подставляется вместо сезона шаблона, когда он не указан; строчными",
  },
  campaign: {
    id: "canonWorks.detail.campaign",
    defaultMessage: "Кампания",
    description: "Название строки: кампания, в рамках которой назначена работа",
  },
  campaignStatus: {
    id: "canonWorks.detail.campaignStatus",
    defaultMessage: "статус: {status}",
    description: "Строка под названием кампании; {status} — статус кампании (черновик, идёт, закрыта…)",
  },
  ruleDeadline: {
    id: "canonWorks.detail.ruleDeadline",
    defaultMessage: "правило {version}, срок по «{kind}»",
    description: "Строка под названием кампании; {version} — версия правила срока, {kind} — по какому событию считается срок",
  },
  whose: {
    id: "canonWorks.detail.whose",
    defaultMessage: "Чьё окно",
    description: "Название строки: хозяйство, у которого открыто окно работы (срок выполнения)",
  },
  unknownName: {
    id: "canonWorks.detail.unknownName",
    defaultMessage: "имя неизвестно",
    description: "Подставляется, когда названия хозяйства нет; строчными",
  },
  windowFrom: {
    id: "canonWorks.detail.window.from",
    defaultMessage: "начало не задано",
    description: "Начало окна работы не задано; строчными",
  },
  windowTo: {
    id: "canonWorks.detail.window.to",
    defaultMessage: "конца нет",
    description: "Конец окна работы не задан; строчными",
  },
  noOverdueHint: {
    id: "canonWorks.detail.window.noOverdueHint",
    defaultMessage: "У работы нет срока — просрочки по ней не существует",
    description: "Подсказка у пометки «просрочки не существует»",
  },
  noOverdue: {
    id: "canonWorks.detail.window.noOverdue",
    defaultMessage: "просрочки не существует",
    description: "Пометка: у работы нет срока, поэтому и просрочки нет",
  },
  overdue: {
    id: "canonWorks.detail.window.overdue",
    defaultMessage: "просрочено",
    description: "Отметка статуса: срок работы прошёл, а отчитались не все; коротко",
  },
  notOverdue: {
    id: "canonWorks.detail.window.notOverdue",
    defaultMessage: "не просрочено",
    description: "Пометка: срок не нарушен; коротко",
  },
  doneTitle: {
    id: "canonWorks.detail.done.title",
    defaultMessage: "Что сделано, а что нет",
    description: "Заголовок карточки с итогом по работе",
  },
  nounField: {
    id: "canonWorks.detail.done.nounField",
    defaultMessage: "{count, plural, one {контур} few {контура} many {контуров} other {контура}}",
    description: "Слово после числа под полосой «отчитались» (контуры работы), без самого числа",
  },
  nounWork: {
    id: "canonWorks.detail.done.nounWork",
    defaultMessage: "{count, plural, one {работа на поле} few {работы на полях} many {работ на полях} other {работы на полях}}",
    description: "Слово после числа под полосой «отчитались» (работы на полях), без самого числа",
  },
  noContours: {
    id: "canonWorks.detail.done.noContours",
    defaultMessage: "у этой работы нет работ на полях: охват кампании не назначал её на эту землю",
    description: "Пояснение под пустой полосой счёта по контурам работы",
  },
  worksOnFields: {
    id: "canonWorks.detail.done.worksOnFields",
    defaultMessage: "Работы на полях",
    description: "Подпись второго счёта на карточке работы: сколько работ на полях выполнено",
  },
  noWorksOnFields: {
    id: "canonWorks.detail.done.noWorksOnFields",
    defaultMessage: "работ на полях по этой работе в текущем срезе нет",
    description: "Пояснение под пустым счётом работ на полях; срез — снимок данных на дату",
  },
  areaClass: {
    id: "canonWorks.detail.done.areaClass",
    defaultMessage: "класс строк, по которым сложены гектары",
    description: "Подпись рядом с бейджем достоверности: класс записей, из которых посчитана площадь",
  },
  contoursTitle: {
    id: "canonWorks.detail.contours.title",
    defaultMessage: "Контуры работы",
    description: "Заголовок карточки со списком контуров работы",
  },
  contoursEmpty: {
    id: "canonWorks.detail.contours.empty",
    defaultMessage: "Контуров у работы нет",
    description: "Заголовок пустого списка контуров работы",
  },
  contoursEmptyText: {
    id: "canonWorks.detail.contours.emptyText",
    defaultMessage: "Охват кампании не назначал эту работу ни на один контур в текущем срезе.",
    description: "Пояснение к пустому списку контуров работы",
  },
  contoursCaption: {
    id: "canonWorks.detail.contours.caption",
    defaultMessage: "Одна строка — одна работа на поле. Отчёт и суждение стоят рядом и не сводятся к «итоговому» значению: при расхождении в базе живут обе записи, и выбрать между ними за человека выдача не вправе.",
    description: "Подпись (для чтения с экрана) таблицы контуров работы",
  },
  colContour: {
    id: "canonWorks.detail.contours.colContour",
    defaultMessage: "Контур",
    description: "Заголовок колонки: номер контура",
  },
  colState: {
    id: "canonWorks.detail.contours.colState",
    defaultMessage: "Состояние",
    description: "Заголовок колонки: состояние контура по работе",
  },
  colReport: {
    id: "canonWorks.detail.contours.colReport",
    defaultMessage: "Отчёт",
    description: "Заголовок колонки: отчёт по контуру",
  },
  colBy: {
    id: "canonWorks.detail.contours.colBy",
    defaultMessage: "Кто внёс",
    description: "Заголовок колонки: автор записи",
  },
  colArea: {
    id: "canonWorks.detail.contours.colArea",
    defaultMessage: "Гектары",
    description: "Заголовок колонки: площадь в гектарах (узкая числовая колонка)",
  },
  colVerdict: {
    id: "canonWorks.detail.contours.colVerdict",
    defaultMessage: "Суждение",
    description: "Заголовок колонки: суждение проверяющего",
  },
  noData: {
    id: "canonWorks.detail.noData",
    defaultMessage: "данных нет",
    description: "Пометка в ячейке таблицы: данных нет (это не ноль)",
  },
  noReportDate: {
    id: "canonWorks.detail.noReportDate",
    defaultMessage: "дата не названа",
    description: "Пометка в ячейке отчёта: дата отчёта не указана",
  },
  noAuthor: {
    id: "canonWorks.detail.noAuthor",
    defaultMessage: "автор не записан",
    description: "Пометка в ячейке: автор записи не указан",
  },
  onBehalf: {
    id: "canonWorks.detail.onBehalf",
    defaultMessage: "от имени",
    description: "Бейдж: запись внесена не самим хозяйством, а за него; коротко",
  },
  onBehalfHint: {
    id: "canonWorks.detail.onBehalfHint",
    defaultMessage: "Запись внесена не самим хозяйством, а за него",
    description: "Подсказка у бейджа «от имени»",
  },
  verdictAwaiting: {
    id: "canonWorks.detail.verdict.awaiting",
    defaultMessage: "суждения нет — ждёт проверки",
    description: "Пометка в ячейке суждения: отчёт есть, проверяющий ещё не смотрел",
  },
  verdictNothing: {
    id: "canonWorks.detail.verdict.nothing",
    defaultMessage: "проверять нечего: отчёта нет",
    description: "Пометка в ячейке суждения: отчёта нет, проверять нечего",
  },
  verdictCount: {
    id: "canonWorks.detail.verdict.count",
    defaultMessage: "{count, plural, one {# суждение} few {# суждения} many {# суждений} other {# суждения}}",
    description: "Кнопка в ячейке суждения: открыть список суждений проверяющих по контуру; {count} — число",
  },
  shownOf: {
    id: "canonWorks.detail.contours.shownOf",
    defaultMessage: "Показаны {shown} из {total, plural, one {# контура} few {# контуров} many {# контуров} other {# контуров}}.",
    description: "Подпись под таблицей контуров работы; {shown} — сколько показано, {total} — сколько всего",
  },
  showAll: {
    id: "canonWorks.detail.contours.showAll",
    defaultMessage: "Показать все {total}",
    description: "Кнопка под таблицей контуров работы: показать все строки; {total} — число",
  },
  whoTitle: {
    id: "canonWorks.detail.who.title",
    defaultMessage: "Кто внёс и от чьего имени",
    description: "Заголовок раскрытия: кто вносил отчёты и проверки по работе",
  },
  whoSummary: {
    id: "canonWorks.detail.who.summary",
    defaultMessage: "{reports, plural, one {# отчёт} few {# отчёта} many {# отчётов} other {# отчёта}}, {visits, plural, one {# выезд} few {# выезда} many {# выездов} other {# выезда}}",
    description: "Подпись-сводка раскрытия «Кто внёс…»; {reports} — число отчётов, {visits} — число выездов проверяющих",
  },
  whoNote: {
    id: "canonWorks.detail.who.note",
    defaultMessage: "Авторство — факт записи, а не право смотрящего: пометка «от имени» принадлежит внесённой строке. Роли автора ОТЧЁТА в корпусе нет — её не записывают, в отличие от роли проверяющего; подставить сюда сегодняшнее полномочие значило бы приписать его прошлой записи.",
    description: "Пояснение в раскрытии «Кто внёс…»; «корпус» — набор данных системы",
  },
  entriesEmpty: {
    id: "canonWorks.detail.entries.empty",
    defaultMessage: "Отчётов по работе не поступало",
    description: "Заголовок пустого списка отчётов по работе",
  },
  entriesEmptyText: {
    id: "canonWorks.detail.entries.emptyText",
    defaultMessage: "Это отсутствие данных, а не подтверждённое невыполнение.",
    description: "Пояснение к пустому списку отчётов по работе",
  },
  entriesCaption: {
    id: "canonWorks.detail.entries.caption",
    defaultMessage: "Массовые отчёты хозяйства, в порядке внесения.",
    description: "Подпись (для чтения с экрана) таблицы массовых отчётов",
  },
  colWhen: {
    id: "canonWorks.detail.entries.colWhen",
    defaultMessage: "Когда внесён",
    description: "Заголовок колонки: когда отчёт внесён",
  },
  colForWhom: {
    id: "canonWorks.detail.entries.colForWhom",
    defaultMessage: "За кого",
    description: "Заголовок колонки: за кого внесён отчёт",
  },
  colReported: {
    id: "canonWorks.detail.entries.colReported",
    defaultMessage: "Что сообщено",
    description: "Заголовок колонки: что сообщено в отчёте",
  },
  colRows: {
    id: "canonWorks.detail.entries.colRows",
    defaultMessage: "Строк",
    description: "Заголовок колонки: число затронутых строк (узкая числовая колонка)",
  },
  noAuthorShort: {
    id: "canonWorks.detail.noAuthorShort",
    defaultMessage: "автора нет",
    description: "Пометка в ячейке: автора нет",
  },
  farmFallbackShort: {
    id: "canonWorks.detail.entries.farmFallback",
    defaultMessage: "хозяйство #{id}",
    description: "Подпись хозяйства без названия в таблице отчётов; {id} — номер",
  },
  farmUnnamed: {
    id: "canonWorks.detail.entries.farmUnnamed",
    defaultMessage: "хозяйство не названо",
    description: "Пометка в таблице отчётов: хозяйство не названо",
  },
  forSelf: {
    id: "canonWorks.detail.entries.forSelf",
    defaultMessage: "за себя",
    description: "Значение в колонке «За кого»: хозяйство внесло отчёт само",
  },
  dayAccuracy: {
    id: "canonWorks.detail.entries.dayAccuracy",
    defaultMessage: "{date}, точность: {precision}",
    description: "Подпись даты события в таблице отчётов; {date} — дата, {precision} — точность даты (день, неделя…)",
  },
  noDayAccuracy: {
    id: "canonWorks.detail.entries.noDayAccuracy",
    defaultMessage: "день не назван, точность: {precision}",
    description: "Подпись даты события в таблице отчётов, когда день не назван; {precision} — точность даты",
  },
  skippedHint: {
    id: "canonWorks.detail.entries.skippedHint",
    defaultMessage: "Строки, которых массовый ввод не коснулся",
    description: "Подсказка у пометки «пропущено»",
  },
  skipped: {
    id: "canonWorks.detail.entries.skipped",
    defaultMessage: "пропущено: {count}",
    description: "Пометка в ячейке числа строк: сколько строк пропущено; {count} — число",
  },
  visitsEmpty: {
    id: "canonWorks.detail.visits.empty",
    defaultMessage: "Выездов проверяющего не было",
    description: "Заголовок пустого списка выездов проверяющих",
  },
  visitsEmptyText: {
    id: "canonWorks.detail.visits.emptyText",
    defaultMessage: "Отсутствие проверки не означает «проверено и не подтвердилось».",
    description: "Пояснение к пустому списку выездов",
  },
  visitsCaption: {
    id: "canonWorks.detail.visits.caption",
    defaultMessage: "Выезды проверяющих. Роль записана вместе с суждением: менеджер хокимията и проверяющий оба вправе, но это не одно и то же — менеджер бывает автором проверяемого отчёта.",
    description: "Подпись (для чтения с экрана) таблицы выездов проверяющих; хокимият — администрация района",
  },
  colVisitWhen: {
    id: "canonWorks.detail.visits.colWhen",
    defaultMessage: "Когда",
    description: "Заголовок колонки выездов: дата выезда",
  },
  colVisitWho: {
    id: "canonWorks.detail.visits.colWho",
    defaultMessage: "Кто",
    description: "Заголовок колонки выездов: проверяющий",
  },
  colVisitRole: {
    id: "canonWorks.detail.visits.colRole",
    defaultMessage: "В каком качестве",
    description: "Заголовок колонки выездов: роль проверяющего",
  },
  colVisitOutcome: {
    id: "canonWorks.detail.visits.colOutcome",
    defaultMessage: "Исход",
    description: "Заголовок колонки выездов: итог проверки",
  },
  whereTitle: {
    id: "canonWorks.detail.where.title",
    defaultMessage: "Куда ведёт эта работа",
    description: "Заголовок раскрытия со ссылками на соседние уровни (массив, хозяйство)",
  },
  whereSummary: {
    id: "canonWorks.detail.where.summary",
    defaultMessage: "{contours, plural, one {# контур} few {# контура} many {# контуров} other {# контура}}, {massifs, plural, one {# массив} few {# массива} many {# массивов} other {# массива}}",
    description: "Подпись-сводка раскрытия «Куда ведёт эта работа»; {contours}, {massifs} — числа",
  },
  whereNote: {
    id: "canonWorks.detail.where.note",
    defaultMessage: "Работа — лист иерархии: агрегатов выше себя она не показывает. Ниже только адреса соседних уровней, посчитанные сервером по её собственным контурам.",
    description: "Пояснение в раскрытии «Куда ведёт эта работа»",
  },
  massifLink: {
    id: "canonWorks.detail.where.massifLink",
    defaultMessage: "Массив {name}",
    description: "Ссылка на массив; {name} — название массива",
  },
  farmLink: {
    id: "canonWorks.detail.where.farmLink",
    defaultMessage: "Хозяйство {name}",
    description: "Ссылка на хозяйство; {name} — название хозяйства или #номер",
  },
  drawerKicker: {
    id: "canonWorks.detail.drawer.kicker",
    defaultMessage: "Суждения проверяющих по этому контуру",
    description: "Подзаголовок боковой панели с суждениями по контуру",
  },
  drawerOpen: {
    id: "canonWorks.detail.drawer.open",
    defaultMessage: "Открыть уровень контура",
    description: "Ссылка внизу боковой панели: перейти на экран контура",
  },
  drawerRole: {
    id: "canonWorks.detail.drawer.role",
    defaultMessage: "в качестве: {role}",
    description: "Строка в боковой панели суждения; {role} — роль проверяющего",
  },
  perItem: {
    id: "canonWorks.detail.drawer.perItem",
    defaultMessage: "поштучно",
    description: "Бейдж: суждение об этом контуре отдельно; коротко",
  },
  perItemHint: {
    id: "canonWorks.detail.drawer.perItemHint",
    defaultMessage: "Поштучное суждение об этом контуре",
    description: "Подсказка у бейджа «поштучно»",
  },
  oneVisit: {
    id: "canonWorks.detail.drawer.oneVisit",
    defaultMessage: "одним выездом",
    description: "Бейдж: проверка внесена одним выездом по хозяйству; коротко",
  },
  oneVisitHint: {
    id: "canonWorks.detail.drawer.oneVisitHint",
    defaultMessage: "Проверка внесена одним выездом по хозяйству, а не осмотром этого контура по отдельности",
    description: "Подсказка у бейджа «одним выездом»",
  },
});

/**
 * Состояние строки контура ОДНОЙ отметкой — и только по тому, что записано.
 *
 * Три исхода различаются намеренно: «проверено» — суждение проверяющего,
 * «ждёт проверки» — отчёт есть, суждения нет, «без отчёта» — данных нет. Ни
 * один из них не означает «не сделано»: этого утверждения в корпусе нет.
 */
function contourStatus(row: ObligationContourOut) {
  if (row.verifications.length > 0) {
    return { status: "verified" as const, label: tr(m.stVerified) };
  }
  if (row.awaiting_verification) {
    return { status: "progress" as const, label: tr(m.stAwaiting) };
  }
  if (row.progress !== null) {
    return { status: "ok" as const, label: tr(m.stReported) };
  }
  return { status: "missing" as const, label: tr(m.stNone) };
}

function contourLabel(row: ObligationContourOut): string {
  const base = row.contour_no || tr(m.workOnField, { id: row.obligation_id });
  return row.contour_part_id
    ? tr(m.partOf, { base, part: row.contour_part_id })
    : base;
}

export function WorkDetail() {
  const t = useT();
  const { workId } = useParams();
  const context = useAnalysisContext();
  const location = useLocation();
  const id = Number(workId);
  const request = useRequest(
    () => api.farmObligation(id, context.params),
    [id, context.key],
  );
  const [showAllContours, setShowAllContours] = useState(false);
  const [picked, setPicked] = useState<number | null>(null);

  if (request.state === "loading") {
    return <DataStatePanel state="loading" title={t(m.loading)} />;
  }
  if (request.error && !request.data) {
    return (
      <DataStatePanel
        state={request.error.status === 404 ? "empty" : "error"}
        title={
          request.error.status === 404
            ? t(m.notFound)
            : t(m.loadFailed)
        }
        action={
          <>
            <Link
              to={{ pathname: levelPath("work"), search: context.linkSearch() }}
            >
              <Button>{t(m.toQueue)}</Button>
            </Link>
            <Button onClick={request.reload}>{t(COMMON.retry)}</Button>
            {/* Работы пилота бывают синтетическими, и без флага демо сервер
                их не видит (404). Сказать это и дать включить флаг честнее,
                чем «такой работы нет»: подменять флаг молча нельзя —
                это срез человека, а не экрана. */}
            {request.error.status === 404 && !context.includeDemo ? (
              <Button onClick={() => context.setIncludeDemo(true)}>
                {t(m.showDemo)}
              </Button>
            ) : null}
          </>
        }
      >
        {request.error.message}
        {request.error.status === 404 && !context.includeDemo
          ? ` ${t(m.demoHidden)}`
          : null}
      </DataStatePanel>
    );
  }
  if (!request.data) return null;

  const detail = request.data;
  const item = detail.item;
  const norm = detail.norm;
  const place = detail.place;
  const note = windowNote(item);
  const contours = detail.contours;
  const rows = showAllContours
    ? { shown: contours, hidden: 0 }
    : preview(contours, DENSITY.tablePreview);
  const pickedRow =
    picked === null
      ? null
      : (contours.find((row) => row.obligation_id === picked) ?? null);

  // Ступень массива. Их бывает несколько: работа адресована ХОЗЯЙСТВУ, а
  // территория — свойство земли, на которую легли обязательства. Тогда ступень
  // называет их число и ссылкой не становится: выбрать один массив за человека
  // нельзя, а показать первый значило бы соврать про остальные.
  const massifs = place?.massifs ?? [];
  const massifStep: TrailStep =
    massifs.length === 1 && massifs[0]
      ? {
          level: "massif",
          label: massifs[0].canonical_name,
          to: {
            pathname: levelPath("massif", massifs[0].id),
            search: context.linkSearch(),
          },
        }
      : {
          level: "massif",
          caption: massifs.length > 1 ? t(m.massifs) : undefined,
          label:
            massifs.length > 1
              ? t(m.massifCount, { count: massifs.length })
              : t(m.massifUnknown),
          to: null,
        };

  return (
    <div className={styles.stack}>
      <PageHeader
        title={item.work_name}
        breadcrumb={
          <LevelTrail
            steps={[
              districtStep(context.linkSearch()),
              massifStep,
              {
                level: "actor",
                label: item.actor.name ?? t(m.farmFallback, { id: item.actor.id }),
                to: {
                  pathname: levelPath("actor", item.actor.id),
                  search: context.linkSearch(),
                },
              },
              { level: "work", label: item.work_name, to: null },
            ]}
          />
        }
        purpose={
          detail.context.as_of
            ? t(m.purposeAsOf, {
                date: formatDate(detail.context.as_of),
                version: detail.context.rules_version ?? t(m.notSpecified),
              })
            : t(m.purposeNow)
        }
        actions={
          <>
            {/* Возврат в СПИСОК с его фильтрами — другой вопрос, чем подъём по
                уровню, поэтому он стоит отдельной кнопкой и несёт
                `location.search` целиком. */}
            <Link
              to={{
                pathname: ROUTES.verification,
                search: context.linkSearch({ farm_obligation_id: item.id }),
              }}
            >
              <Button>{t(m.reviewInQueue)}</Button>
            </Link>
            <Link
              to={{ pathname: levelPath("work"), search: location.search }}
            >
              <Button>{t(m.toQueue)}</Button>
            </Link>
          </>
        }
      />

      {item.demo_batch_id !== null ? (
        <InlineAlert title={t(m.demoAlertTitle)}>
          {t(m.demoAlertText)}
        </InlineAlert>
      ) : null}

      {/* --- Что за работа и чьё окно ------------------------------------ */}

      <Card title={t(m.whatTitle)} large>
        <DefinitionList>
          <dt>{t(m.catalogWork)}</dt>
          <dd>
            {norm ? (
              <>
                {norm.catalog_name}{" "}
                <span className={styles.answerNote}>
                  {t(m.catalogNote, {
                    code: norm.catalog_code,
                    category: workCategoryLabel(norm.category).label,
                  })}
                </span>
              </>
            ) : (
              <Text value={null} fallback={t(m.noCatalog)} />
            )}
          </dd>

          <dt>{t(m.planWork)}</dt>
          <dd>
            {norm ? (
              <>
                №{norm.slot_order_no}{" "}
                <Text value={norm.slot_label} fallback={t(m.noPlanLabel)} />
                <div className={styles.rowWrap}>
                  <span>
                    {norm.template_crop_code
                      ? t(m.templateCrop, {
                          season: norm.template_season_label ?? t(m.seasonUnknown),
                          crop: cropLabel(norm.template_crop_code).label,
                        })
                      : t(m.templateNoCrop, {
                          season: norm.template_season_label ?? t(m.seasonUnknown),
                        })}
                  </span>
                  <Badge tone={norm.slot_is_required ? "attention" : "unknown"}>
                    {norm.slot_is_required ? t(m.required) : t(m.optional)}
                  </Badge>
                </div>
              </>
            ) : (
              <Text value={null} fallback={t(m.noPlan)} />
            )}
          </dd>

          <dt>{t(m.campaign)}</dt>
          <dd>
            {item.campaign_title}
            <div className={styles.rowWrap}>
              <span>
                {t(m.campaignStatus, {
                  status: campaignStatusLabel(item.campaign_status).label,
                })}
              </span>
              {norm?.rule_semver && norm.rule_deadline_kind ? (
                <span>
                  {t(m.ruleDeadline, {
                    version: norm.rule_semver,
                    kind: deadlineKindLabel(norm.rule_deadline_kind).label,
                  })}
                </span>
              ) : null}
              <EvidenceBadge status={item.epistemic_status} />
            </div>
          </dd>

          <dt>{t(m.whose)}</dt>
          <dd>
            <Link
              to={{
                pathname: levelPath("actor", item.actor.id),
                search: context.linkSearch(),
              }}
            >
              <Text value={item.actor.name} fallback={t(m.unknownName)} />
            </Link>
            <div className={styles.rowWrap}>
              <span>
                {item.window_from ? formatDate(item.window_from) : t(m.windowFrom)}
                {" — "}
                {item.window_to ? formatDate(item.window_to) : t(m.windowTo)}
              </span>
              <Badge tone={item.overdue ? "attention" : "unknown"}>
                {note.label}
              </Badge>
              {item.overdue === null ? (
                <span title={t(m.noOverdueHint)}>{t(m.noOverdue)}</span>
              ) : item.overdue ? (
                <StatusMark status="warning" label={t(m.overdue)} />
              ) : (
                <span>{t(m.notOverdue)}</span>
              )}
            </div>
            {note.hint ? <p className={styles.answerNote}>{note.hint}</p> : null}
          </dd>
        </DefinitionList>
      </Card>

      {/* --- Ответ уровня: две оси РАЗДЕЛЬНО ------------------------------ */}

      <Card title={t(m.doneTitle)} large>
        {/* Две оси по КОНТУРАМ работы: это её собственный знаменатель.
            Обязательства стоят рядом, а не вместо: у контура, заявленного
            частями, обязательств несколько, и «3 из 11» в двух знаменателях
            означают разный объём работы. */}
        <DoneSplit
          counts={item.contours}
          noun={m.nounField}
          size="hero"
          absent={
            detail.contours_note ??
            t(m.noContours)
          }
        />
        {detail.contours_note && item.contours.total > 0 ? (
          <p className={styles.answerNote}>{detail.contours_note}</p>
        ) : null}

        <div className={styles.answerSecondary}>
          <div>
            <span className={styles.answerLabel}>{t(m.worksOnFields)}</span>
            <DoneSplit
              counts={item.obligations}
              noun={m.nounWork}
              absent={t(m.noWorksOnFields)}
            />
          </div>
          <div>
            <AreaProgress area={item.area} rounded />
            {item.area.measured?.epistemic ? (
              <p className={styles.answerNote}>
                <EvidenceBadge status={item.area.measured.epistemic} />{" "}
                {t(m.areaClass)}
              </p>
            ) : null}
          </div>
        </div>
        <AxisLegend />
      </Card>

      {/* --- Контуры работы: отчёт и суждение по каждому ------------------ */}

      <Card title={t(m.contoursTitle)}>
        {contours.length === 0 ? (
          <DataStatePanel state="empty" title={t(m.contoursEmpty)}>
            {detail.contours_note ??
              t(m.contoursEmptyText)}
          </DataStatePanel>
        ) : (
          <>
            <div className={styles.scrollX}>
              <table className={styles.plainTable}>
                <caption>{t(m.contoursCaption)}</caption>
                <thead>
                  <tr>
                    <th scope="col">{t(m.colContour)}</th>
                    <th scope="col">{t(m.colState)}</th>
                    <th scope="col">{t(m.colReport)}</th>
                    <th scope="col">{t(m.colBy)}</th>
                    <th scope="col" className={styles.num}>
                      {t(m.colArea)}
                    </th>
                    <th scope="col">{t(m.colVerdict)}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.shown.map((row) => {
                    const mark = contourStatus(row);
                    return (
                      <tr key={row.obligation_id}>
                        <th scope="row">
                          <Link
                            to={{
                              pathname: levelPath("contour", row.contour_id),
                              search: context.linkSearch(),
                            }}
                          >
                            {contourLabel(row)}
                          </Link>
                        </th>
                        <td>
                          <StatusMark status={mark.status} label={mark.label} />
                        </td>
                        <td>
                          {row.progress === null ? (
                            <Text value={null} fallback={t(m.noData)} />
                          ) : (
                            <>
                              {progressLabel(row.progress).label}
                              <div className={styles.rowWrap}>
                                <span>
                                  {row.reported_on
                                    ? formatDate(row.reported_on)
                                    : t(m.noReportDate)}
                                </span>
                              </div>
                            </>
                          )}
                        </td>
                        <td>
                          <Text value={row.reported_by} fallback={t(m.noAuthor)} />
                          {row.entered_on_behalf ? (
                            <div className={styles.rowWrap}>
                              <Badge
                                tone="attention"
                                title={t(m.onBehalfHint)}
                              >
                                {t(m.onBehalf)}
                              </Badge>
                            </div>
                          ) : null}
                        </td>
                        <td className={styles.num}>
                          <Area microha={row.area_microha} rounded />
                        </td>
                        <td>
                          {row.verifications.length === 0 ? (
                            <Text
                              value={null}
                              fallback={
                                row.awaiting_verification
                                  ? t(m.verdictAwaiting)
                                  : t(m.verdictNothing)
                              }
                            />
                          ) : (
                            <Button onClick={() => setPicked(row.obligation_id)}>
                              {t(m.verdictCount, { count: row.verifications.length })}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {rows.hidden > 0 ? (
              <p className={styles.issueMessage}>
                {t(m.shownOf, { shown: rows.shown.length, total: contours.length })}{" "}
                <Button onClick={() => setShowAllContours(true)}>
                  {t(m.showAll, { total: contours.length })}
                </Button>
              </p>
            ) : null}
          </>
        )}
      </Card>

      {/* --- Кто внёс и от чьего имени ------------------------------------ */}

      <Disclosure
        title={t(m.whoTitle)}
        summary={t(m.whoSummary, {
          reports: detail.entries.length,
          visits: detail.visits.length,
        })}
      >
        <p className={styles.issueMessage}>{t(m.whoNote)}</p>

        {detail.entries.length === 0 ? (
          <DataStatePanel state="empty" title={t(m.entriesEmpty)}>
            {t(m.entriesEmptyText)}
          </DataStatePanel>
        ) : (
          <div className={styles.scrollX}>
            <table className={styles.plainTable}>
              <caption>{t(m.entriesCaption)}</caption>
              <thead>
                <tr>
                  <th scope="col">{t(m.colWhen)}</th>
                  <th scope="col">{t(m.colBy)}</th>
                  <th scope="col">{t(m.colForWhom)}</th>
                  <th scope="col">{t(m.colReported)}</th>
                  <th scope="col" className={styles.num}>
                    {t(m.colRows)}
                  </th>
                </tr>
              </thead>
              <tbody>
                {detail.entries.map((entry) => (
                  <tr key={entry.id}>
                    <th scope="row">{formatDate(entry.entry_time)}</th>
                    <td>
                      <Text value={entry.reported_by} fallback={t(m.noAuthorShort)} />
                    </td>
                    <td>
                      {entry.entered_on_behalf ? (
                        <>
                          <Badge tone="attention">{t(m.onBehalf)}</Badge>{" "}
                          <Text
                            value={entry.represented_actor_name}
                            fallback={
                              entry.represented_actor_id
                                ? t(m.farmFallbackShort, { id: entry.represented_actor_id })
                                : t(m.farmUnnamed)
                            }
                          />
                        </>
                      ) : (
                        t(m.forSelf)
                      )}
                    </td>
                    <td>
                      {progressLabel(entry.progress).label}
                      <div className={styles.rowWrap}>
                        <span>
                          {entry.occurred_on
                            ? t(m.dayAccuracy, {
                                date: formatDate(entry.occurred_on),
                                precision: datePrecisionLabel(entry.date_precision).label,
                              })
                            : t(m.noDayAccuracy, {
                                precision: datePrecisionLabel(entry.date_precision).label,
                              })}
                        </span>
                      </div>
                      {entry.note ? (
                        <p className={styles.answerNote}>{entry.note}</p>
                      ) : null}
                    </td>
                    <td className={styles.num}>
                      <Count value={entry.applied_count} />
                      {entry.skipped_count > 0 ? (
                        <div className={styles.rowWrap}>
                          <span title={t(m.skippedHint)}>
                            {t(m.skipped, { count: entry.skipped_count })}
                          </span>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {detail.visits.length === 0 ? (
          <DataStatePanel state="empty" title={t(m.visitsEmpty)}>
            {t(m.visitsEmptyText)}
          </DataStatePanel>
        ) : (
          <div className={styles.scrollX}>
            <table className={styles.plainTable}>
              <caption>{t(m.visitsCaption)}</caption>
              <thead>
                <tr>
                  <th scope="col">{t(m.colVisitWhen)}</th>
                  <th scope="col">{t(m.colVisitWho)}</th>
                  <th scope="col">{t(m.colVisitRole)}</th>
                  <th scope="col">{t(m.colVisitOutcome)}</th>
                  <th scope="col" className={styles.num}>
                    {t(m.colRows)}
                  </th>
                </tr>
              </thead>
              <tbody>
                {detail.visits.map((visit) => (
                  <tr key={visit.id}>
                    <th scope="row">{formatDate(visit.occurred_on)}</th>
                    <td>
                      <Text value={visit.verifier} fallback={t(m.noAuthorShort)} />
                    </td>
                    <td>{roleView(visit.verifier_role).label}</td>
                    <td>
                      {outcomePast(visit.outcome).label}
                      {visit.note ? (
                        <p className={styles.answerNote}>{visit.note}</p>
                      ) : null}
                    </td>
                    <td className={styles.num}>
                      <Count value={visit.applied_count} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Disclosure>

      {/* --- Ключи уровней: куда ведёт эта работа ------------------------- */}

      <Disclosure
        title={t(m.whereTitle)}
        summary={t(m.whereSummary, {
          contours: place?.contour_ids.length ?? 0,
          massifs: massifs.length,
        })}
      >
        <p className={styles.issueMessage}>{t(m.whereNote)}</p>
        <ul className={styles.legendList}>
          {massifs.map((massif) => (
            <li key={massif.id}>
              <Link
                to={{
                  pathname: levelPath("massif", massif.id),
                  search: context.linkSearch(),
                }}
              >
                {t(m.massifLink, { name: massif.canonical_name })}
              </Link>
            </li>
          ))}
          <li>
            <Link
              to={{
                pathname: levelPath("actor", item.actor.id),
                search: context.linkSearch(),
              }}
            >
              {t(m.farmLink, { name: item.actor.name ?? `#${item.actor.id}` })}
            </Link>
          </li>
        </ul>
      </Disclosure>

      <DetailDrawer
        open={pickedRow !== null}
        title={pickedRow ? contourLabel(pickedRow) : ""}
        kicker={t(m.drawerKicker)}
        onClose={() => setPicked(null)}
        footer={
          pickedRow ? (
            <Link
              to={{
                pathname: levelPath("contour", pickedRow.contour_id),
                search: context.linkSearch(),
              }}
            >
              {t(m.drawerOpen)}
            </Link>
          ) : null
        }
      >
        {pickedRow ? (
          <DefinitionList>
            {pickedRow.verifications.map((claim) => (
              <div key={claim.verification_id}>
                <dt>{formatDate(claim.occurred_on)}</dt>
                <dd>
                  {outcomePast(claim.outcome).label}
                  <div className={styles.rowWrap}>
                    <Text value={claim.verifier} fallback={t(m.noAuthorShort)} />
                    <span>
                      {t(m.drawerRole, { role: roleView(claim.verifier_role).label })}
                    </span>
                    {claim.from_bulk_verification_id === null ? (
                      <Badge tone="unknown" title={t(m.perItemHint)}>
                        {t(m.perItem)}
                      </Badge>
                    ) : (
                      <Badge
                        tone="unknown"
                        title={t(m.oneVisitHint)}
                      >
                        {t(m.oneVisit)}
                      </Badge>
                    )}
                  </div>
                  {claim.note ? (
                    <p className={styles.answerNote}>{claim.note}</p>
                  ) : null}
                </dd>
              </div>
            ))}
          </DefinitionList>
        ) : null}
      </DetailDrawer>

      {/* Карман пятого уровня: вопрос об этой работе, не уходя с неё. */}
      <LevelPocket level="work" objectId={id} />
    </div>
  );
}
